import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { imgUrl } from '../utils/imgUrl';
import { get_products } from '../store/reducers/homeReducer';
import {
    get_card_products, delete_card_product, messageClear, quantity_inc, quantity_dec,
    add_to_card, add_to_wishlist,
} from '../store/reducers/cardReducer';
import { DISPLAY, Container, formatQ, priceLabel, Bag, Heart, Truck, Undo, ArrowRight, Plus, Close } from '../components/home/ui';
import { pbtn } from '../components/account/shared';

// Carrito (diseño carrito.html). Todo pasa por los thunks de siempre y, tras
// cada cambio, se vuelve a pedir el carrito: el backend decide precio, stock
// y qué líneas están agotadas.

const Steps = () => (
    <div className='flex items-center gap-[10px] text-[13px] font-bold text-[#64748b] mb-[22px] flex-wrap'>
        {['Carrito', 'Entrega', 'Pago'].map((l, i) => (
            <React.Fragment key={l}>
                {i > 0 && <b className='w-7 h-[1.5px] bg-[#e8e6e1]' />}
                <span className={`inline-flex items-center gap-2 ${i === 0 ? 'text-[#1e3a8a]' : ''}`}>
                    <i className={`not-italic w-6 h-6 rounded-full grid place-items-center text-[12px] border-[1.5px] ${i === 0 ? 'bg-[#1e3a8a] border-[#1e3a8a] text-white' : 'bg-white border-[#e8e6e1]'}`}>{i + 1}</i>{l}
                </span>
            </React.Fragment>
        ))}
    </div>
);

// Sin `size` ocupa su columna (96 px, 80 en móvil); con `size`, un cuadro fijo.
const Img = ({ src, size }) => (
    <div className={`rounded-[14px] overflow-hidden border border-[#f1efea] bg-[#faf9f6] grid place-items-center flex-shrink-0 ${size ? '' : 'w-24 h-24 sm:w-20 sm:h-20'}`} style={size ? { width: size, height: size } : undefined}>
        {src ? <img src={imgUrl(src)} alt='' className='w-full h-full object-cover' /> : <span className='text-[28px] opacity-30'>🐾</span>}
    </div>
);

const Card = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userInfo } = useSelector(state => state.auth);
    const { card_products, price, buy_product_item, shipping_fee, outofstock_products, successMessage, errorMessage } = useSelector(state => state.card);
    const { products } = useSelector(state => state.home);
    const [busy, setBusy] = useState(null);       // id de la línea que se está actualizando
    const [undo, setUndo] = useState(null);       // { text, items: [{variantId, quantity}] }
    const undoTimer = useRef(null);

    const lines = card_products[0]?.products || [];
    const agotados = outofstock_products || [];
    const refresh = () => userInfo && dispatch(get_card_products(userInfo.id));

    useEffect(() => {
        // El carrito vive en el servidor, por cliente: sin sesión no hay carrito.
        if (!userInfo) { navigate('/login', { replace: true }); return; }
        refresh();
        if (!products.length) dispatch(get_products());
    }, []);

    // Los mensajes de éxito del carrito solo disparan la recarga; los errores sí se muestran.
    useEffect(() => {
        if (successMessage) { dispatch(messageClear()); refresh(); }
        if (errorMessage) { toast.error(errorMessage); dispatch(messageClear()); refresh(); }
    }, [successMessage, errorMessage]);

    const run = (id, action) => {
        setBusy(id);
        return dispatch(action).finally(() => { setBusy(null); refresh(); });
    };

    const showUndo = (text, items) => {
        clearTimeout(undoTimer.current);
        setUndo({ text, items });
        undoTimer.current = setTimeout(() => setUndo(null), 5000);
    };

    const remove = (ln) => {
        run(ln._id, delete_card_product(ln._id));
        showUndo('Producto eliminado', [{ variantId: ln.productInfo._id, quantity: ln.quantity }]);
    };

    const saveForLater = (ln) => {
        const p = ln.productInfo;
        dispatch(add_to_wishlist({
            userId: userInfo.id, productId: p._id, name: p.name, price: p.price,
            image: imgUrl(p.image), discount: 0, rating: 0, slug: p.slug,
        }));
        run(ln._id, delete_card_product(ln._id));
        showUndo('Guardado en favoritos', [{ variantId: p._id, quantity: ln.quantity }]);
    };

    const clearAll = () => {
        const todas = [...lines, ...agotados];
        todas.forEach(ln => dispatch(delete_card_product(ln._id)));
        showUndo('Vaciaste el carrito', todas.map(ln => ({ variantId: (ln.productInfo || ln.products?.[0])._id, quantity: ln.quantity })));
    };

    const doUndo = () => {
        clearTimeout(undoTimer.current);
        undo.items.forEach(it => dispatch(add_to_card({ userId: userInfo.id, quantity: it.quantity, productId: it.variantId })));
        setUndo(null);
    };

    const checkout = () => {
        navigate('/shipping', {
            state: { products: card_products, price, shipping_fee, items: buy_product_item },
        });
    };

    const enCarrito = new Set([...lines.map(l => l.productInfo._id), ...agotados.map(l => l.products?.[0]?._id)]);
    const sugeridos = products.filter(p => !enCarrito.has(p._id) && p.price > 0 && (p.stock > 0 || p.bajo_pedido)).slice(0, 4);
    const unidades = lines.reduce((s, l) => s + Number(l.quantity || 0), 0);

    const Line = ({ ln, fuera }) => {
        const p = fuera ? ln.products[0] : ln.productInfo;
        const stock = Number(p.stock || 0);
        const topado = !p.bajo_pedido && ln.quantity >= stock;
        const detail = `/product/details/${p.slug}`;
        return (
            <div className={`grid grid-cols-[96px_minmax(0,1fr)_auto] sm:grid-cols-[80px_minmax(0,1fr)] gap-[18px] sm:gap-[14px] py-5 border-b border-[#f1efea] last:border-b-0 items-center ${busy === ln._id ? 'opacity-60' : ''}`}>
                <Link to={detail}><Img src={p.image || p.images?.[0]} /></Link>
                <div className='min-w-0'>
                    <Link to={detail}><h3 className='text-[15.5px] font-bold text-[#0f172a] mt-0 mb-1 leading-[1.3] hover:text-[#0891b2]'>{p.name}</h3></Link>
                    {fuera
                        ? <span className='inline-flex items-center gap-[6px] text-[12.5px] font-bold text-[#ef4444] mt-[6px]'><span className='w-[6px] h-[6px] rounded-full bg-current' />Agotado · no se incluye en el total</span>
                        : p.bajo_pedido && stock <= 0
                            ? <span className='inline-flex items-center gap-[6px] text-[12.5px] font-bold text-[#b45309] mt-[6px]'><span className='w-[6px] h-[6px] rounded-full bg-current' />Bajo pedido · llega en hasta {p.dias_entrega} días hábiles</span>
                            : <span className={`inline-flex items-center gap-[6px] text-[12.5px] font-bold mt-[6px] ${stock <= 3 ? 'text-[#b45309]' : 'text-[#16a34a]'}`}>
                                <span className='w-[6px] h-[6px] rounded-full bg-current' />{stock <= 3 ? `Quedan ${stock} disponibles` : 'En stock'}
                              </span>}
                    <div className='flex gap-[14px] mt-[10px] flex-wrap'>
                        {!fuera && (
                            <button type='button' onClick={() => saveForLater(ln)} className='text-[13px] font-semibold text-[#64748b] inline-flex items-center gap-[6px] hover:text-[#0891b2]'>
                                <Heart className='w-[15px] h-[15px]' />Guardar para después
                            </button>
                        )}
                        <button type='button' onClick={() => remove(ln)} className='text-[13px] font-semibold text-[#64748b] inline-flex items-center gap-[6px] hover:text-[#ef4444]'>
                            <Close className='w-[15px] h-[15px]' />Eliminar
                        </button>
                    </div>
                </div>
                <div className='flex flex-col items-end gap-3 sm:col-span-2 sm:flex-row sm:justify-between sm:items-center'>
                    <div className='text-right'>
                        <b className='block text-[19px] font-extrabold text-[#1e3a8a]' style={DISPLAY}>{p.price > 0 ? formatQ(p.price * ln.quantity) : priceLabel(0)}</b>
                        {ln.quantity > 1 && p.price > 0 && <small className='block text-[12px] text-[#64748b]'>{formatQ(p.price)} c/u</small>}
                    </div>
                    {!fuera && (
                        <div className='flex items-center border-[1.5px] border-[#e8e6e1] rounded-[12px] h-[42px] bg-white'>
                            <button type='button' aria-label={`Quitar uno de ${p.name}`} disabled={ln.quantity <= 1 || busy === ln._id}
                                onClick={() => run(ln._id, quantity_dec(ln._id))}
                                className='w-[38px] h-full text-[17px] text-[#1e3a8a] font-semibold hover:text-[#0891b2] disabled:text-[#e8e6e1] disabled:cursor-not-allowed'>−</button>
                            <span className='min-w-[28px] text-center font-bold text-[#0f172a]' aria-live='polite'>{ln.quantity}</span>
                            <button type='button' aria-label={`Agregar uno de ${p.name}`} disabled={topado || busy === ln._id}
                                onClick={() => run(ln._id, quantity_inc(ln._id))}
                                className='w-[38px] h-full text-[17px] text-[#1e3a8a] font-semibold hover:text-[#0891b2] disabled:text-[#e8e6e1] disabled:cursor-not-allowed'>+</button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (!userInfo) return null;
    const vacio = lines.length === 0 && agotados.length === 0;

    return (
        <div className='w-full bg-white'>
            <Header />
            <div className='bg-[#faf9f6] pt-8 pb-20 border-t border-[#f1efea]'>
                <Container>
                    <Steps />
                    <h1 className='text-[#1e3a8a] font-bold mt-0 mb-[18px] tracking-[-.02em] leading-[1.1]' style={{ ...DISPLAY, fontSize: 'clamp(26px, 3vw, 34px)' }}>Tu carrito</h1>

                    {vacio ? (
                        <div className='bg-white border border-[#f1efea] rounded-[20px] text-center px-6 py-16'>
                            <div className='w-[72px] h-[72px] rounded-full bg-[#ecfeff] text-[#0891b2] grid place-items-center mx-auto mb-4'><Bag className='w-[30px] h-[30px]' strokeWidth={2} /></div>
                            <h2 className='text-[#1e3a8a] text-[24px] font-bold mt-0 mb-[6px]' style={DISPLAY}>Tu carrito está vacío</h2>
                            <p className='text-[#64748b] mt-0 mb-[22px]'>Agrega productos y aparecerán aquí.</p>
                            <Link to='/shops' className={pbtn}>Ir a la tienda</Link>
                        </div>
                    ) : (
                        <div className='grid grid-cols-[minmax(0,1fr)_380px] lg:grid-cols-1 gap-6 items-start'>
                            <div>
                                <div className='bg-white border border-[#f1efea] rounded-[20px] px-[22px] py-[18px] mb-[14px] flex items-center gap-[10px] text-[14px] text-[#334155]'>
                                    <Truck className='w-5 h-5 text-[#0891b2] flex-shrink-0' />
                                    <span>El envío a todo Guatemala cuesta <b className='text-[#0f172a]'>{formatQ(shipping_fee)}</b>. Lo pagas junto con tu pedido, contra entrega.</span>
                                </div>

                                {lines.length > 0 && (
                                    <div className='bg-white border border-[#f1efea] rounded-[20px] px-[22px] py-[6px]'>
                                        <div className='flex justify-between items-center py-[14px] border-b border-[#f1efea]'>
                                            <h2 className='text-[#1e3a8a] text-[20px] font-bold m-0' style={DISPLAY}>{unidades} {unidades === 1 ? 'producto' : 'productos'}</h2>
                                            <button type='button' onClick={clearAll} className='text-[13px] font-bold text-[#64748b] hover:text-[#ef4444]'>Vaciar carrito</button>
                                        </div>
                                        {lines.map(ln => <Line key={ln._id} ln={ln} />)}
                                    </div>
                                )}

                                {agotados.length > 0 && (
                                    <div className='bg-white border border-[#fee2e2] rounded-[20px] px-[22px] py-[6px] mt-[14px]'>
                                        <div className='flex justify-between items-center py-[14px] border-b border-[#f1efea]'>
                                            <h2 className='text-[#b91c1c] text-[18px] font-bold m-0' style={DISPLAY}>Sin disponibilidad ({agotados.length})</h2>
                                            {lines.length === 0 && <button type='button' onClick={clearAll} className='text-[13px] font-bold text-[#64748b] hover:text-[#ef4444]'>Vaciar carrito</button>}
                                        </div>
                                        {agotados.map(ln => <Line key={ln._id} ln={ln} fuera />)}
                                    </div>
                                )}
                            </div>

                            <aside className='bg-white border border-[#f1efea] rounded-[20px] p-6 sticky top-[150px] lg:static'>
                                <h2 className='text-[#1e3a8a] text-[20px] font-bold mt-0 mb-4' style={DISPLAY}>Resumen del pedido</h2>
                                <div className='flex justify-between text-[14.5px] text-[#334155] py-[7px]'><span>Subtotal ({unidades})</span><b className='text-[#0f172a]'>{formatQ(price)}</b></div>
                                <div className='flex justify-between text-[14.5px] text-[#334155] py-[7px]'><span>Envío</span><b className='text-[#0f172a]'>{formatQ(shipping_fee)}</b></div>
                                <div className='flex justify-between items-baseline border-t border-[#f1efea] mt-[10px] pt-4'>
                                    <span className='text-[15px] font-bold text-[#0f172a]'>Total</span>
                                    <b className='text-[30px] font-extrabold text-[#1e3a8a] tracking-[-.02em]' style={DISPLAY}>{formatQ(lines.length ? price + shipping_fee : 0)}</b>
                                </div>
                                <div className='text-[12.5px] text-[#64748b] text-right mt-[2px]'>IVA incluido</div>
                                <button type='button' onClick={checkout} disabled={!lines.length}
                                    className='w-full h-[54px] mt-[18px] rounded-[14px] bg-[#0891b2] hover:bg-[#0e7490] text-white font-bold text-[15.5px] flex items-center justify-center gap-[10px] shadow-[0_10px_24px_-12px_rgba(8,145,178,.6)] transition-all disabled:opacity-45 disabled:cursor-not-allowed'>
                                    Continuar con la entrega <ArrowRight className='w-[18px] h-[18px]' />
                                </button>
                                {!lines.length && <p className='text-[12.5px] text-[#b91c1c] text-center mt-2 mb-0'>Los productos agotados no se pueden comprar.</p>}
                                <Link to='/shops' className='block text-center mt-3 text-[13.5px] font-bold text-[#1e3a8a]'>Seguir comprando</Link>
                                <div className='flex flex-col gap-[10px] mt-[18px] pt-4 border-t border-[#f1efea] text-[13px] text-[#334155]'>
                                    <div className='flex gap-[10px] items-center'><Bag className='w-[17px] h-[17px] text-[#0891b2] flex-shrink-0' strokeWidth={2} />Pagas contra entrega, en efectivo</div>
                                    <div className='flex gap-[10px] items-center'><Undo className='w-[17px] h-[17px] text-[#0891b2] flex-shrink-0' />Cambios y devoluciones en 30 días</div>
                                </div>
                            </aside>
                        </div>
                    )}

                    {sugeridos.length > 0 && (
                        <div className='mt-10'>
                            <h2 className='text-[#1e3a8a] text-[22px] font-bold mt-0 mb-1' style={DISPLAY}>Complementa tu pedido</h2>
                            <p className='mt-0 mb-4 text-[#64748b] text-[14px]'>Otros productos de la tienda que te pueden servir.</p>
                            <div className='grid grid-cols-4 lg:grid-cols-2 sm:grid-cols-1 gap-[14px]'>
                                {sugeridos.map(p => (
                                    <div key={p._id} className='bg-white border border-[#f1efea] rounded-[20px] flex gap-3 items-center p-[14px]'>
                                        <Link to={`/product/details/${p.slug}`}><Img src={p.images?.[0] || p.image} size={64} /></Link>
                                        <div className='min-w-0'>
                                            <Link to={`/product/details/${p.slug}`} className='text-[13.5px] font-bold text-[#0f172a] leading-[1.3] hover:text-[#0891b2]'
                                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.name}</Link>
                                            <div className='font-extrabold text-[#1e3a8a] text-[15px] mt-[3px]' style={DISPLAY}>{formatQ(p.price)}</div>
                                        </div>
                                        <button type='button' aria-label={`Agregar ${p.name}`}
                                            onClick={() => dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: p._id })).unwrap().then(() => toast.success('Agregado al carrito')).catch(() => {})}
                                            className='ml-auto w-9 h-9 rounded-[10px] bg-[#ecfeff] text-[#0891b2] grid place-items-center flex-shrink-0 hover:bg-[#0891b2] hover:text-white'>
                                            <Plus className='w-4 h-4' />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Container>
            </div>

            <div role='status' className={`fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#0f172a] text-white pl-5 pr-3 py-3 rounded-[14px] text-[14px] flex gap-4 items-center z-[100] transition-all duration-[250ms] ${undo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5 pointer-events-none'}`}>
                <span>{undo?.text}</span>
                <button type='button' onClick={doUndo} className='bg-white/[.14] px-[14px] py-2 rounded-[10px] font-bold text-[13px]'>Deshacer</button>
            </div>

            <Footer />
        </div>
    );
};

export default Card;
